import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import useAppUser from "../hooks/useAppUser";
import APP_CONFIG from "../app.config";
import createUserIfNotExists from "../features/users/helpers/createUserIfNotExists";
import { getMessagingInstance } from "@/services/firebase";
import saveFcmToken from "../features/users/helpers/saveFcmToken";
import { detectPlatformForFcm } from "@/services/api/notificationService";
import { logLogin, logSignup } from "../analytics.config";
import { useUser } from "../contexts/UserContext";
import { isE2EMockAuthEnabled, setE2EAuthState } from "../utils/e2eAuth";
import { motion } from "framer-motion";
import { auth, googleProvider, db } from "@/services/firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  signInWithPopup,
  sendPasswordResetEmail,
} from "firebase/auth";
import { getDoc, doc } from "firebase/firestore";
import toast from "react-hot-toast";
import TermsConsentField from "../features/legal/components/TermsConsentField";
import OptionalConsentsSection from "../features/legal/components/OptionalConsentsSection";
import { recordLegalAcceptance } from "../features/legal/consentService";
import { createEmptyConsentSelection, getConsentErrorMessages } from "../features/legal/legalConfig";
import { getAnalyticsConsentStatus } from "../features/legal/legalStorage";

const isUserBlocked = (userData) => {
  if (!userData) {
    return false;
  }

  return userData.actif === false || userData.active === false || userData.deleted === true || userData.archived === true;
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLogin, setIsLogin] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [consentSelection, setConsentSelection] = useState(() => {
    const selection = createEmptyConsentSelection();
    selection.analyticsCookiesAccepted = getAnalyticsConsentStatus() === "accepted";
    return selection;
  });
  const [consentErrors, setConsentErrors] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { firebaseUser, role, loading } = useAppUser();
  const { logout } = useUser();

  // Handle potential redirect passed via location state (e.g., from an email link)
  const from = location.state?.from?.pathname || null;

  useEffect(() => {
    if (firebaseUser && !loading) {
      const redirectPath = from || (role ? APP_CONFIG.roleRedirects[role] : "/") || "/";
      navigate(redirectPath, { replace: true });
    }
  }, [firebaseUser, role, loading, navigate, from]);

  const handleFcmToken = async (userId) => {
    const messaging = await getMessagingInstance();
    if (!messaging) return;

    try {
      const { getToken } = await import("firebase/messaging");
      const registration = await navigator.serviceWorker.ready;
      const fcmToken = await getToken(messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (fcmToken) {
        const platform = detectPlatformForFcm();
        await saveFcmToken(userId, fcmToken, platform);
      }
    } catch (error) {
      console.error("Erreur lors de la gestion du token FCM:", error);
    }
  };

  const handleAuth = async () => {
    if (!email || !password) {
      toast.error("Veuillez remplir tous les champs.");
      return;
    }
    
    if (!isLogin) {
      const errors = getConsentErrorMessages(consentSelection);
      if (errors.length > 0) {
        setConsentErrors(errors);
        return;
      }
      setConsentErrors([]);
    }

    setIsAuthenticating(true);
    try {
      if (isE2EMockAuthEnabled()) {
        const normalizedEmail = email.trim().toLowerCase();
        const mockRole = normalizedEmail.includes("admin")
          ? "Admin"
          : normalizedEmail.includes("patron")
            ? "Patron"
            : "Employe";

        setE2EAuthState({
          uid: `e2e-${mockRole.toLowerCase()}`,
          email: normalizedEmail || "e2e@example.com",
          displayName: `E2E ${mockRole}`,
          role: mockRole,
        });

        const redirectPath = from || APP_CONFIG.roleRedirects[mockRole] || "/";
        navigate(redirectPath, { replace: true });
        return;
      }

      let userCredential;
      if (isLogin) {
        userCredential = await signInWithEmailAndPassword(auth, email, password);
        await logLogin("email");
        toast.success("Content de vous revoir !");
      } else {
        userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await recordLegalAcceptance({
          source: "signup_email",
          email,
          selection: consentSelection,
        });
        await logSignup("email");
        await logLogin("email");
        toast.success("Bienvenue chez Jacana !");
      }
      const user = userCredential.user;

      await createUserIfNotExists(user.uid, user.email);
      await handleFcmToken(user.uid);

      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists() && isUserBlocked(userDoc.data())) {
        await logout();
        toast.error(APP_CONFIG.blockedUserMessage);
        return;
      }
    } catch (error) {
      if (isLogin && (error.code === "auth/user-not-found" || error.code === "auth/invalid-credential")) {
        toast.error(
          (t) => (
            <span>
              Ce compte n'existe pas encore.{" "}
              <button
                onClick={() => {
                  setIsLogin(false);
                  toast.dismiss(t.id);
                }}
                className="font-bold underline"
              >
                Créer un compte ?
              </button>
            </span>
          ),
          { duration: 6000 }
        );
      } else {
        toast.error("Erreur d'authentification. Veuillez vérifier vos identifiants.");
      }
      console.error(error);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleGoogleLogin = async () => {
    if (!isLogin) {
      const errors = getConsentErrorMessages(consentSelection);
      if (errors.length > 0) {
        setConsentErrors(errors);
        return;
      }
      setConsentErrors([]);
    }

    setIsAuthenticating(true);
    try {
      if (isE2EMockAuthEnabled()) {
        const mockRole = "Developpeur";
        setE2EAuthState({
          uid: "e2e-google-dev",
          email: "e2e-google@example.com",
          displayName: "E2E Google Dev",
          role: mockRole,
        });
        const redirectPath = from || APP_CONFIG.roleRedirects[mockRole] || "/";
        navigate(redirectPath, { replace: true });
        return;
      }

      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const additionalUserInfo = getAdditionalUserInfo(result);

      if (additionalUserInfo?.isNewUser) {
        if (!isLogin) {
          await recordLegalAcceptance({
            source: "signup_google",
            email: user.email,
            selection: consentSelection,
          });
        }
        await logSignup("google");
        toast.success("Bienvenue chez Jacana !");
      } else {
        toast.success("Content de vous revoir !");
      }
      await logLogin("google");

      await createUserIfNotExists(user.uid, user.email);
      await handleFcmToken(user.uid);

      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists() && isUserBlocked(userDoc.data())) {
        await logout();
        toast.error(APP_CONFIG.blockedUserMessage);
        return;
      }
    } catch (error) {
       toast.error("Erreur de connexion avec Google.");
       console.error(error);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      toast.error("Veuillez renseigner votre email d'abord.");
      return;
    }
    setIsAuthenticating(true);
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success("Email de réinitialisation envoyé !");
    } catch (error) {
      toast.error("Erreur lors de la réinitialisation.");
      console.error(error);
    } finally {
      setIsAuthenticating(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-[#8F9B82] rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
      <div className="absolute top-0 left-0 -mt-20 -ml-20 w-96 h-96 bg-[#B5A492] rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
      <div className="absolute -bottom-32 left-1/2 transform -translate-x-1/2 w-96 h-96 bg-[#E8E2D2] rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-4000"></div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="glass-panel p-8 md:p-10 flex flex-col items-center">
          
          <h1 className="font-serif text-3xl md:text-4xl text-brand-primary mb-2 text-center">
            {isLogin ? "Bon retour" : "Bienvenue"}
          </h1>
          
          <p className="text-brand-primary/70 text-center mb-8">
            {isLogin ? "Connectez-vous pour accéder à votre espace" : "Créez votre compte en quelques secondes"}
          </p>

          <div className="w-full space-y-4">
            <button
              onClick={handleGoogleLogin}
              disabled={isAuthenticating}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border border-brand-primary/20 bg-brand-paper/50 hover:bg-brand-paper hover:border-brand-primary/40 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAuthenticating ? (
                <div className="w-5 h-5 border-2 border-brand-primary/30 border-t-brand-primary rounded-full animate-spin" />
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  <span>Continuer avec Google</span>
                </>
              )}
            </button>

            <div className="flex items-center gap-4 my-6">
              <div className="flex-1 h-px bg-brand-primary/10"></div>
              <span className="text-xs text-brand-primary/40 uppercase tracking-widest">ou</span>
              <div className="flex-1 h-px bg-brand-primary/10"></div>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleAuth();
              }}
              className="space-y-4"
              noValidate
            >
              <div>
                <label className="block text-sm text-brand-primary/80 mb-1 ml-1">Email</label>
                <input
                  type="email"
                  className="input-premium"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="votre@email.com"
                  autoComplete="email"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-1 ml-1">
                   <label className="block text-sm text-brand-primary/80">Mot de passe</label>
                   {isLogin && (
                      <button
                        type="button"
                        onClick={handlePasswordReset}
                        className="text-xs text-brand-primary/60 hover:text-brand-primary transition-colors"
                        disabled={isAuthenticating}
                      >
                        Oublié ?
                      </button>
                   )}
                </div>
                <input
                  type="password"
                  className="input-premium"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  autoComplete={isLogin ? "current-password" : "new-password"}
                />
              </div>

              <button
                type="submit"
                className="btn-premium w-full mt-6 py-3"
                disabled={isAuthenticating}
              >
                {isAuthenticating ? (
                  <div className="w-5 h-5 border-2 border-brand-paper/30 border-t-brand-paper rounded-full animate-spin mx-auto" />
                ) : (
                  isLogin ? "Se connecter" : "S'inscrire"
                )}
              </button>
            </form>

            {!isLogin ? (
              <div className="mt-6 rounded-[24px] border border-brand-primary/10 bg-white/70 p-4">
                <TermsConsentField
                  value={consentSelection}
                  onChange={setConsentSelection}
                  errorMessages={consentErrors}
                />
                <div className="mt-4">
                  <OptionalConsentsSection value={consentSelection} onChange={setConsentSelection} />
                </div>
              </div>
            ) : null}

            <div className="mt-6 text-center text-sm text-brand-primary/60">
              {isLogin ? "Pas encore de compte ?" : "Déjà un compte ?"}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="ml-2 font-medium text-brand-primary hover:underline"
                disabled={isAuthenticating}
              >
                {isLogin ? "Inscrivez-vous" : "Connectez-vous"}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
